import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Building2, User, MapPin, Hash, Phone, Receipt, ShieldCheck, CalendarDays,
  Home as HomeIcon, KeyRound, Store, MoreHorizontal, LogOut, Briefcase, Cake, Gift, UserCheck,
} from "lucide-react";

const GATED_ROLES = ["client", "worker", "lead_worker"];

const BUSINESS_TYPES = [
  { id: "property_management", label: "Property Management", icon: Building2 },
  { id: "landlord", label: "Landlord / Portfolio", icon: HomeIcon },
  { id: "airbnb", label: "Airbnb / Short-Let", icon: KeyRound },
  { id: "estate_agent", label: "Estate Agent", icon: Store },
  { id: "other", label: "Other", icon: MoreHorizontal },
];

const FIELD_ICONS = {
  company_name: Building2,
  first_name: User,
  last_name: User,
  business_address: MapPin,
  company_registration_number: Hash,
  phone: Phone,
  address: MapPin,
  postcode: Hash,
  date_of_birth: Cake,
};

const FIELD_PLACEHOLDERS = {
  company_name: "Company Ltd",
  first_name: "First name",
  last_name: "Last name",
  business_address: "Registered company address",
  company_registration_number: "e.g. 12345678",
  phone: "07xxx xxxxxx",
  address: "House number and street",
  postcode: "e.g. SA1 1AA",
};

const inputType = (field) => (field === "phone" ? "tel" : field === "date_of_birth" ? "date" : "text");

const GateLoader = () => (
  <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="profile-gate-loader">
    <div className="w-8 h-8 border-[3px] border-[#0099FF] border-t-transparent rounded-full animate-spin" />
  </div>
);

export default function ProfileCompletionGate({ children }) {
  const { user, token, refreshUser, logout } = useAuth();
  const isBusiness = user?.role === "client" && user?.client_type === "business";
  const isGated = GATED_ROLES.includes(user?.role);

  const [checking, setChecking] = useState(isGated);
  const [missing, setMissing] = useState([]);
  const [bizType, setBizType] = useState("");
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/profile-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const m = res.data.profile_complete ? [] : (res.data.missing_fields || []);
      setMissing(m);
      setBizType(res.data.business_type || "");
      const init = {};
      m.forEach(({ field }) => { init[field] = ""; });
      if (m.some((x) => x.field === "vat_number")) init.vat_registered = true;
      setValues(init);
    } catch (e) {
      // Never hard-lock a user out on a network/auth error
      setMissing([]);
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => {
    if (isGated) fetchStatus();
    else setChecking(false);
  }, [isGated, fetchStatus]);

  const set = (field, val) => setValues((prev) => ({ ...prev, [field]: val }));

  const effectiveType = values.business_type || bizType;
  const showICal = effectiveType === "airbnb";

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    for (const m of missing) {
      if (m.field === "vat_number") continue;
      if (!String(values[m.field] || "").trim()) {
        toast.error(`Please fill in: ${m.label}`);
        return;
      }
    }
    const vatInMissing = missing.some((x) => x.field === "vat_number");
    if (vatInMissing && values.vat_registered && !String(values.vat_number || "").trim()) {
      toast.error("Enter your VAT number or turn off VAT registration");
      return;
    }

    const payload = {};
    missing.forEach((m) => {
      if (m.field !== "vat_number") payload[m.field] = values[m.field];
    });
    if (vatInMissing) {
      payload.vat_registered = !!values.vat_registered;
      if (values.vat_registered) payload.vat_number = values.vat_number;
    }
    // Optional Airbnb / Booking.com iCal links (only for short-let hosts)
    if (showICal) {
      if (String(values.airbnb_ical_url || "").trim()) payload.airbnb_ical_url = values.airbnb_ical_url.trim();
      if (String(values.booking_ical_url || "").trim()) payload.booking_ical_url = values.booking_ical_url.trim();
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/complete-profile`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Profile completed. Thank you!");
      await refreshUser?.();
      setMissing([]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to save. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isGated) return children;
  if (checking) return <GateLoader />;
  if (!missing.length) return children;

  const vatInMissing = missing.some((x) => x.field === "vat_number");
  const textFields = missing.filter((m) => m.field !== "business_type" && m.field !== "vat_number");
  const needsBusinessType = missing.some((m) => m.field === "business_type");

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4 sm:p-6" data-testid="profile-completion-gate">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6 sm:p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl bg-[#0099FF]/15 flex items-center justify-center flex-shrink-0">
              {isBusiness ? <Briefcase size={22} className="text-[#0099FF]" /> : <UserCheck size={22} className="text-[#0099FF]" />}
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-heading text-white leading-tight" data-testid="profile-gate-title">
                {isBusiness ? "Complete your business profile" : "Complete your profile"}
              </h1>
              <p className="text-[#A1A1AA] text-sm">We need a few required details before you can continue.</p>
            </div>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 my-5">
            <p className="text-amber-300 text-xs leading-relaxed" data-testid="profile-gate-notice">
              {isBusiness
                ? "For business accounts, all fields below are required (for legal and billing purposes). You can't access your dashboard until they're completed."
                : "Full name, phone number, address and date of birth are required on every account. You can't access your dashboard until they're completed."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="profile-gate-form">
            {textFields.map((m) => {
              const Icon = FIELD_ICONS[m.field] || User;
              return (
                <div className="space-y-1.5" key={m.field}>
                  <Label htmlFor={`gate-${m.field}`} className="text-white text-sm">
                    {m.label} <span className="text-red-400">*</span>
                  </Label>
                  <div className="relative">
                    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" size={18} />
                    <Input
                      id={`gate-${m.field}`}
                      type={inputType(m.field)}
                      max={m.field === "date_of_birth" ? new Date().toISOString().split("T")[0] : undefined}
                      placeholder={FIELD_PLACEHOLDERS[m.field] || ""}
                      value={values[m.field] || ""}
                      onChange={(e) => set(m.field, e.target.value)}
                      className="pl-10 bg-[#1A1A1A] border-[#262626] text-white placeholder:text-[#6B6B6B] [color-scheme:dark]"
                      data-testid={`profile-gate-${m.field}`}
                    />
                  </div>
                  {m.field === "date_of_birth" && (
                    <p className="flex items-start gap-1.5 text-[11px] text-[#0099FF] leading-snug" data-testid="profile-gate-dob-hint">
                      <Gift size={13} className="mt-0.5 shrink-0" />
                      Your date of birth is only used to send you birthday discounts and promotions when your special day is near.
                    </p>
                  )}
                </div>
              );
            })}

            {needsBusinessType && (
              <div className="space-y-2">
                <Label className="text-white text-sm">Business category <span className="text-red-400">*</span></Label>
                <div className="grid grid-cols-2 gap-2.5">
                  {BUSINESS_TYPES.map((bt) => {
                    const Icon = bt.icon;
                    const active = values.business_type === bt.id;
                    return (
                      <button
                        key={bt.id}
                        type="button"
                        onClick={() => set("business_type", bt.id)}
                        className={`p-3 rounded-lg border-2 text-center transition-colors flex flex-col items-center gap-1.5 ${
                          active
                            ? "border-[#0099FF] bg-[#0099FF]/10 text-white"
                            : "border-[#262626] bg-[#1A1A1A] text-[#A1A1AA] hover:border-[#404040]"
                        }`}
                        data-testid={`profile-gate-business-type-${bt.id}`}
                      >
                        <Icon size={20} />
                        <span className="font-semibold text-xs leading-tight">{bt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {vatInMissing && (
              <div className="space-y-3 border border-[#262626] rounded-lg p-3 bg-[#1A1A1A]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Receipt size={18} className="text-[#0099FF]" />
                    <span className="text-white text-sm font-medium">VAT registered?</span>
                  </div>
                  <Switch
                    checked={!!values.vat_registered}
                    onCheckedChange={(checked) => set("vat_registered", checked)}
                    data-testid="profile-gate-vat-switch"
                  />
                </div>
                {values.vat_registered && (
                  <div className="space-y-1.5">
                    <Label htmlFor="gate-vat_number" className="text-white text-sm">VAT Number <span className="text-red-400">*</span></Label>
                    <Input
                      id="gate-vat_number"
                      type="text"
                      placeholder="e.g. GB123456789"
                      value={values.vat_number || ""}
                      onChange={(e) => set("vat_number", e.target.value)}
                      className="bg-[#1A1A1A] border-[#262626] text-white placeholder:text-[#6B6B6B]"
                      data-testid="profile-gate-vat_number"
                    />
                  </div>
                )}
              </div>
            )}

            {showICal && (
              <div className="border border-[#0099FF]/30 bg-[#0099FF]/5 rounded-lg p-3 space-y-3" data-testid="profile-gate-ical-section">
                <div className="flex items-center gap-2">
                  <CalendarDays size={16} className="text-[#0099FF]" />
                  <span className="text-white text-sm font-medium">Short-Let Calendars (optional)</span>
                </div>
                <p className="text-xs text-[#A1A1AA]">Paste your Airbnb and/or Booking.com iCal links so we can automatically sync bookings and schedule changeover cleaning between guests.</p>
                <div className="space-y-1.5">
                  <Label htmlFor="gate-airbnb_ical_url" className="text-white text-xs">Airbnb iCal link</Label>
                  <Input
                    id="gate-airbnb_ical_url"
                    type="url"
                    placeholder="https://www.airbnb.com/calendar/ical/....ics"
                    value={values.airbnb_ical_url || ""}
                    onChange={(e) => set("airbnb_ical_url", e.target.value)}
                    className="bg-[#1A1A1A] border-[#262626] text-white placeholder:text-[#6B6B6B] text-sm"
                    data-testid="profile-gate-airbnb_ical_url"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="gate-booking_ical_url" className="text-white text-xs">Booking.com iCal link</Label>
                  <Input
                    id="gate-booking_ical_url"
                    type="url"
                    placeholder="https://admin.booking.com/.../ical.ics"
                    value={values.booking_ical_url || ""}
                    onChange={(e) => set("booking_ical_url", e.target.value)}
                    className="bg-[#1A1A1A] border-[#262626] text-white placeholder:text-[#6B6B6B] text-sm"
                    data-testid="profile-gate-booking_ical_url"
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-[#0099FF] hover:bg-[#0088DD] text-white font-semibold py-3 h-auto"
              disabled={submitting}
              data-testid="profile-gate-submit-btn"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2"><ShieldCheck size={18} /> Save & continue</span>
              )}
            </Button>
          </form>

          <button
            onClick={logout}
            className="w-full mt-4 text-[#A1A1AA] hover:text-white text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="profile-gate-logout-btn"
          >
            <LogOut size={15} /> Log out
          </button>
        </div>
      </motion.div>
    </div>
  );
}
